/**
 * EKKO Wallet Monitor Service
 * 
 * This service monitors specified wallets for transactions and provides alerts when conditions are met.
 */

import { adminSupabase } from './supabaseService';
import { WalletTracker, WalletTrackerAlert } from '../types/tradingBot';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import solanaTrackerPollingService from './solanaTrackerPollingService';

export class WalletMonitorService {
  private walletAddress: string | null = null;
  private userId: string | null = null;
  private isActive = false;
  private eventEmitter = new EventEmitter();
  
  // Cached data
  private trackers: WalletTracker[] = [];
  private alerts: WalletTrackerAlert[] = [];
  private trackedWallets: Set<string> = new Set();
  
  // Transaction deduplication
  private processedTransactions = new Set<string>();
  
  /**
   * Initialize the wallet monitor service
   */
  public async initialize(walletAddress: string): Promise<void> {
    try {
      this.walletAddress = walletAddress;
      
      // Get user ID from wallet address
      const { data: user, error: userError } = await adminSupabase
        .from('users')
        .select('id')
        .eq('wallet_address', walletAddress)
        .single();
        
      if (userError) {
        throw new Error(`Failed to fetch user ID: ${userError.message}`);
      }
      
      this.userId = user.id;
      
      // Load trackers and alerts
      await this.loadTrackers();
      await this.loadAlerts();
      
      console.log(`WalletMonitorService initialized for wallet: ${walletAddress}`);
    } catch (error) {
      console.error('Error initializing WalletMonitorService:', error);
      this.eventEmitter.emit('error', { 
        type: 'initialization',
        message: error instanceof Error ? error.message : 'Unknown initialization error'
      });
      throw error;
    }
  }
  
  /**
   * Set the active state of the wallet monitor
   */
  public async setActive(active: boolean): Promise<void> {
    this.isActive = active;
    console.log(`Wallet Monitor ${active ? 'activated' : 'deactivated'}`);
    
    if (active) {
      // Set up wallet tracking when activated
      this.setupWalletTracking();
    } else {
      // Clear tracking when deactivated
      this.clearWalletTracking();
    }
    
    this.eventEmitter.emit('active', { active });
  }
  
  /**
   * Clean up resources
   */
  public cleanup(): void {
    this.isActive = false;
    this.clearWalletTracking();
    
    this.walletAddress = null;
    this.userId = null;
    this.trackers = [];
    this.alerts = [];
    this.processedTransactions.clear();
    
    console.log('Wallet Monitor cleaned up');
  }
  
  /**
   * Load trackers from database
   */
  private async loadTrackers(): Promise<void> {
    if (!this.userId) {
      throw new Error('User ID not initialized');
    }
    
    const { data, error } = await adminSupabase
      .from('wallet_trackers')
      .select('*')
      .eq('user_id', this.userId);
      
    if (error) {
      console.error('Error loading wallet trackers:', error);
      this.eventEmitter.emit('error', { 
        type: 'database', 
        message: `Failed to load trackers: ${error.message}` 
      });
      return;
    }
    
    this.trackers = data;
    this.eventEmitter.emit('trackers', { trackers: this.trackers });
    console.log(`Loaded ${this.trackers.length} wallet trackers`);
  }
  
  /**
   * Load alerts from database
   */
  private async loadAlerts(): Promise<void> {
    if (!this.userId) {
      throw new Error('User ID not initialized');
    }
    
    const { data, error } = await adminSupabase
      .from('wallet_tracker_alerts')
      .select('*')
      .eq('user_id', this.userId)
      .order('created_at', { ascending: false })
      .limit(100);
      
    if (error) {
      console.error('Error loading wallet tracker alerts:', error);
      this.eventEmitter.emit('error', { 
        type: 'database', 
        message: `Failed to load alerts: ${error.message}` 
      });
      return;
    }
    
    this.alerts = data;
    this.eventEmitter.emit('alerts', { alerts: this.alerts });
    console.log(`Loaded ${this.alerts.length} wallet tracker alerts`);
  }
  
  /**
   * Set up tracking for monitored wallets
   */
  private setupWalletTracking(): void {
    // Clear existing tracking
    this.clearWalletTracking();
    
    // For each active tracker, set up tracking
    const activeTrackers = this.trackers.filter(tracker => tracker.enabled);
    
    for (const tracker of activeTrackers) {
      if (!tracker.wallet_address) continue;
      
      this.trackedWallets.add(tracker.wallet_address);
    }
    
    console.log(`Set up tracking for ${this.trackedWallets.size} wallets`);
  }
  
  /**
   * Clear tracking for all wallets
   */
  private clearWalletTracking(): void {
    this.trackedWallets.clear();
    console.log('Cleared wallet tracking');
  }
  
  /**
   * Get all wallet trackers
   */
  public async getTrackers(): Promise<WalletTracker[]> {
    if (!this.userId) {
      return [];
    }
    
    await this.loadTrackers();
    return this.trackers;
  }
  
  /**
   * Save a wallet tracker to the database
   */
  public async saveTracker(tracker: WalletTracker): Promise<WalletTracker> {
    if (!this.userId || !this.walletAddress) {
      throw new Error('Not initialized');
    }
    
    // If tracker has ID, update it
    if (tracker.id) {
      // Make sure it belongs to this user
      if (!this.trackers.some(t => t.id === tracker.id)) {
        throw new Error('Tracker does not belong to this user');
      }
      
      const { data, error } = await adminSupabase
        .from('wallet_trackers')
        .update({
          name: tracker.name,
          wallet_address: tracker.wallet_address,
          description: tracker.description,
          enabled: tracker.enabled,
          track_buys: tracker.track_buys,
          track_sells: tracker.track_sells,
          min_amount_usd: tracker.min_amount_usd,
          alert_type: tracker.alert_type,
          token_whitelist: tracker.token_whitelist,
          token_blacklist: tracker.token_blacklist,
          updated_at: new Date().toISOString()
        })
        .eq('id', tracker.id)
        .select()
        .single();
        
      if (error) {
        console.error('Error updating tracker:', error);
        throw error;
      }
      
      // Update local cache
      const index = this.trackers.findIndex(t => t.id === tracker.id);
      if (index !== -1) {
        this.trackers[index] = data;
      }
      
      // Update wallet tracking if active
      if (this.isActive) {
        this.setupWalletTracking();
      }
      
      this.eventEmitter.emit('trackers', { trackers: this.trackers });
      return data;
            } else {
      // Create new tracker
      const newTracker: Omit<WalletTracker, 'id' | 'created_at' | 'updated_at'> = {
        user_id: this.userId,
        wallet_address: tracker.wallet_address,
        name: tracker.name,
        description: tracker.description,
        enabled: tracker.enabled || true,
        track_buys: tracker.track_buys !== false,
        track_sells: tracker.track_sells !== false,
        min_amount_usd: tracker.min_amount_usd || 0,
        alert_type: tracker.alert_type || 'both',
        token_whitelist: tracker.token_whitelist || [],
        token_blacklist: tracker.token_blacklist || []
      };
      
      const { data, error } = await adminSupabase
        .from('wallet_trackers')
        .insert(newTracker)
        .select()
        .single();
        
      if (error) {
        console.error('Error creating tracker:', error);
        throw error;
      }
      
      // Update local cache
      this.trackers.push(data);
      
      // Update wallet tracking if active
      if (this.isActive) {
        this.setupWalletTracking();
      }
      
      this.eventEmitter.emit('trackers', { trackers: this.trackers });
      return data;
    }
  }
  
  /**
   * Delete a wallet tracker from the database
   */
  public async deleteTracker(trackerId: string): Promise<boolean> {
    if (!this.userId) {
      throw new Error('Not initialized');
    }
    
    const { error } = await adminSupabase
      .from('wallet_trackers')
      .delete()
      .eq('id', trackerId)
      .eq('user_id', this.userId);
      
    if (error) {
      console.error('Error deleting tracker:', error);
      throw error;
    }
    
    // Update local cache
    this.trackers = this.trackers.filter(tracker => tracker.id !== trackerId);
    
    // Update wallet tracking if active
    if (this.isActive) {
      this.setupWalletTracking();
    }
    
    this.eventEmitter.emit('trackers', { trackers: this.trackers });
    return true;
  }
  
  /**
   * Process a wallet transaction from a tracked wallet
   */
  public processWalletTransaction(walletAddress: string, transaction: any): void {
    if (!this.isActive || !this.walletAddress || !this.userId) return;
    
    // Check if this is a tracked wallet
    if (!this.trackedWallets.has(walletAddress)) {
                return;
            }

    // Check if we've already processed this transaction
    if (this.processedTransactions.has(transaction.tx)) {
                return;
            }

    // Add to processed transactions to prevent duplicates
    this.processedTransactions.add(transaction.tx);
    
    console.log(`Wallet Monitor: Processing transaction from ${walletAddress}`, transaction);
    
    // Find trackers that match this wallet
    const matchingTrackers = this.trackers.filter(tracker => {
      if (!tracker.enabled) return false;
      if (tracker.wallet_address !== walletAddress) return false;
      
      // Check transaction type (buy/sell)
      if (transaction.type === 'buy' && !tracker.track_buys) return false;
      if (transaction.type === 'sell' && !tracker.track_sells) return false;
      
      // Check minimum USD amount
      const amountUsd = parseFloat(transaction.volume || transaction.solVolume || '0');
      if (amountUsd < (tracker.min_amount_usd || 0)) return false;
      
      // Check token whitelist/blacklist if we have token info
      const token = transaction.token?.to || transaction.token?.from;
      
      if (token?.address) {
        // Check whitelist
        if (Array.isArray(tracker.token_whitelist) && tracker.token_whitelist.length > 0) {
          if (!tracker.token_whitelist.includes(token.address)) {
            return false;
          }
        }
        
        // Check blacklist
        if (Array.isArray(tracker.token_blacklist) && tracker.token_blacklist.includes(token.address)) {
          return false;
        }
      }
      
      return true;
    });
    
    if (matchingTrackers.length === 0) {
      console.log(`No matching trackers found for transaction from ${walletAddress}`);
      return;
    }
    
    // Generate alerts for each matching tracker
    for (const tracker of matchingTrackers) {
      this.generateAlert(tracker, transaction);
    }
  }
  
  /**
   * Generate an alert from a transaction
   */
  private async generateAlert(tracker: WalletTracker, transaction: any): Promise<void> {
    if (!this.userId || !this.walletAddress) return;
    
    // Get token information from transaction
    const token = transaction.type === 'buy' 
      ? transaction.token?.to 
      : transaction.token?.from;
    
    // Format the alert message
    const tokenName = token?.name || token?.symbol || 'Unknown Token';
    const tokenSymbol = token?.symbol || '???';
    const amount = token?.amount || '???';
    const timeAgo = formatTimeAgo(new Date(transaction.time));
    
    // Create alert message
    let alertMessage = `${tracker.name} ${transaction.type === 'buy' ? 'bought' : 'sold'} ${amount} ${tokenSymbol}`;
    
    // Add USD value if available
    if (transaction.volume || transaction.solVolume) {
      const volumeUsd = parseFloat(transaction.volume || transaction.solVolume || '0').toFixed(2);
      alertMessage += ` ($${volumeUsd})`;
    }
    
    alertMessage += ` ${timeAgo}`;
    
    console.log(`Generated alert: ${alertMessage}`);
    
    // Create the alert record
    const alert: Omit<WalletTrackerAlert, 'id' | 'created_at' | 'updated_at'> = {
      user_id: this.userId,
      wallet_tracker_id: tracker.id,
      wallet_address: tracker.wallet_address,
      transaction_type: transaction.type,
      transaction_signature: transaction.tx,
      token_mint: token?.address || '',
      token_name: tokenName,
      token_symbol: tokenSymbol,
      amount: amount?.toString() || '',
      price_usd: transaction.priceUsd?.toString() || '',
      volume_usd: (transaction.volume || transaction.solVolume || '0').toString(),
      timestamp: transaction.time,
      message: alertMessage,
      is_read: false
    };
    
    // Save the alert to the database
    try {
      const { data, error } = await adminSupabase
        .from('wallet_tracker_alerts')
        .insert(alert)
        .select()
        .single();
        
      if (error) {
        console.error('Error creating alert:', error);
        return;
      }
      
      // Add to local cache
      this.alerts.unshift(data);
      
      // Keep local cache at reasonable size
      if (this.alerts.length > 100) {
        this.alerts.pop();
      }
      
      // Send notification based on alert type
      this.sendNotification(tracker, data);
      
      // Emit event
      this.eventEmitter.emit('newAlert', { alert: data });
      this.eventEmitter.emit('alerts', { alerts: this.alerts });
    } catch (error) {
      console.error('Error generating alert:', error);
    }
  }
  
  /**
   * Send a notification based on the tracker's alert type
   */
  private sendNotification(tracker: WalletTracker, alert: WalletTrackerAlert): void {
    // Determine alert type
    const alertType = tracker.alert_type || 'both';
    
    // Send browser notification
    if (alertType === 'browser' || alertType === 'both') {
      this.sendBrowserNotification(alert);
    }
    
    // Send email notification (would require separate email service)
    if (alertType === 'email' || alertType === 'both') {
      // this.sendEmailNotification(alert);
      console.log(`Would send email notification for alert ${alert.id}`);
    }
  }
  
  /**
   * Send a browser notification
   */
  private sendBrowserNotification(alert: WalletTrackerAlert): void {
    // Dispatch a custom event that the UI can listen for
if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ekko:notification', {
        detail: {
          title: `Wallet Alert: ${alert.token_symbol}`,
          message: alert.message,
          type: 'info',
          data: alert
        }
      }));
    }
  }
  
  /**
   * Mark an alert as read
   */
  public async markAlertRead(alertId: string): Promise<boolean> {
    if (!this.userId) {
      return false;
    }
    
    const { error } = await adminSupabase
      .from('wallet_tracker_alerts')
      .update({ is_read: true })
      .eq('id', alertId)
      .eq('user_id', this.userId);
      
    if (error) {
      console.error('Error marking alert as read:', error);
      return false;
    }
    
    // Update local cache
    const alertIndex = this.alerts.findIndex(a => a.id === alertId);
    if (alertIndex !== -1) {
      this.alerts[alertIndex].is_read = true;
      this.eventEmitter.emit('alerts', { alerts: this.alerts });
    }
    
    return true;
  }
  
  /**
   * Mark all alerts as read
   */
  public async markAllAlertsRead(): Promise<boolean> {
    if (!this.userId) {
      return false;
    }
    
    const { error } = await adminSupabase
      .from('wallet_tracker_alerts')
      .update({ is_read: true })
      .eq('user_id', this.userId)
      .eq('is_read', false);
      
    if (error) {
      console.error('Error marking all alerts as read:', error);
      return false;
    }
    
    // Update local cache
    this.alerts.forEach(alert => {
      alert.is_read = true;
    });
    
    this.eventEmitter.emit('alerts', { alerts: this.alerts });
    return true;
  }
  
  /**
   * Get alerts for a specific tracker
   */
  public async getAlertsForTracker(trackerId: string): Promise<WalletTrackerAlert[]> {
    if (!this.userId) {
      return [];
    }
    
    const { data, error } = await adminSupabase
      .from('wallet_tracker_alerts')
      .select('*')
      .eq('user_id', this.userId)
      .eq('wallet_tracker_id', trackerId)
      .order('created_at', { ascending: false })
      .limit(100);
      
    if (error) {
      console.error('Error getting alerts for tracker:', error);
      return [];
    }
    
    return data;
  }
  
  /**
   * Get unread alert count
   */
  public getUnreadAlertCount(): number {
    return this.alerts.filter(alert => !alert.is_read).length;
  }
  
  /**
   * Register an event listener
   */
  public on(event: string, callback: (data: any) => void): () => void {
    this.eventEmitter.on(event, callback);
    return () => this.eventEmitter.off(event, callback);
  }
  
  /**
   * Get debugging information
   */
  public getDebugInfo(): any {
    return {
      isActive: this.isActive,
      walletAddress: this.walletAddress,
      userId: this.userId,
      trackerCount: this.trackers.length,
      alertCount: this.alerts.length,
      trackedWalletCount: this.trackedWallets.size,
      processedTransactionCount: this.processedTransactions.size,
      unreadAlertCount: this.getUnreadAlertCount()
    };
  }
}

// Create a singleton instance
const walletMonitorService = new WalletMonitorService();

// Export standalone functions for backward compatibility
export const monitorWallet = async (walletAddress: string): Promise<void> => {
  console.log(`Setting up monitoring for wallet: ${walletAddress}`);
  try {
    // This is just a placeholder - you can add wallet monitoring implementation here
    // For now we'll just return a success message
  } catch (error) {
    console.error(`Error monitoring wallet ${walletAddress}:`, error);
  }
};

export const stopMonitoringWallet = async (walletAddress: string): Promise<void> => {
  console.log(`Stopping monitoring for wallet: ${walletAddress}`);
  try {
    // This is just a placeholder - you can add wallet monitoring cleanup implementation here
    // For now we'll just return a success message
  } catch (error) {
    console.error(`Error stopping monitoring for wallet ${walletAddress}:`, error);
  }
};

export const refreshWalletBalances = async (walletAddress: string): Promise<void> => {
  console.log(`Refreshing balances for wallet: ${walletAddress}`);
  try {
    // This is just a placeholder - you can add wallet balance refresh implementation here
    // For now we'll just return a success message
  } catch (error) {
    console.error(`Error refreshing balances for wallet ${walletAddress}:`, error);
  }
};

// Fix the formatDistance function to use a simple string for now
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  if (diffSec < 60) {
    return `${diffSec} seconds ago`;
  } else if (diffMin < 60) {
    return `${diffMin} minutes ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hours ago`;
  } else {
    return `${diffDay} days ago`;
  }
}

export default walletMonitorService;
